"use client";

import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
} from "@tanstack/react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Columns3,
  Rows3,
  RotateCcw,
  Search,
  SearchX,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

import { SortableColumnItem } from "./sortable-column-item";
import { SortableTableHead } from "./sortable-table-head";
import { SortableFilterItem } from "./sortable-filter-item";
import { useTablePersistence } from "./use-table-persistence";
import { MultiSelectFilter } from "@/components/recipients/ui/multi-select-filter";
import { DateFieldFilter } from "@/components/ui/date-field-filter";
import { SavedViewsMenu, type ViewState } from "@/components/ui/saved-views-menu";
import type {
  UnifiedDataTableProps,
  BulkActionConfig,
  CustomFilterConfig,
} from "./types";

const DEFAULT_INTERACTIVE_SELECTOR =
  'button, input, [role="switch"], [role="checkbox"], a, [data-radix-collection-item]';

export function UnifiedDataTable<TData, TValue, TSearchState = unknown>({
  columns,
  data,
  localStoragePrefix,
  columnDisplayNames = {},
  columnDefaults,
  enableSearch = true,
  searchPlaceholder = "Search...",
  searchHintText,
  globalFilterFn = "includesString",
  onSearchChange,
  searchValue,
  onSortChange,
  onFilterChange,
  onFilteredRowsChange,
  onFilterStateChange,
  filters,
  filterDefaults,
  customFilters,
  customFiltersActive,
  onClearCustomFilters,
  dateFilters,
  enableDensityToggle = true,
  enableRowSelection = false,
  externalRowSelection,
  onExternalRowSelectionChange,
  externalAllAcrossPagesSelected,
  onExternalAllAcrossPagesSelectedChange,
  bulkActions,
  renderBulkActions,
  rowBehavior,
  getRowId,
  advancedSearch,
  pagination,
  clientPagination,
  defaultSorting,
  tableMeta,
  emptyState,
  renderToolbarLeft,
  renderToolbarCenter,
  renderToolbarRight,
  renderFooterStatus,
  sanitizePersistedState,
  maxHeight = "calc(100vh - 380px)",
  minHeight,
  fillHeight = false,
  card = true,
  noResultsText = "No results found.",
  isFetching = false,
  entityNoun = 'items',
  savedViewsKey,
}: UnifiedDataTableProps<TData, TValue, TSearchState>) {
  // --- Persistence Hook ---
  const isControlled = externalRowSelection !== undefined;
  const persistence = useTablePersistence({
    prefix: localStoragePrefix,
    columnDefaults,
    filterDefaults,
    defaultSorting,
    // When the parent wired `onSortChange` it owns sorting server-side via a
    // URL/state query param — don't let localStorage restore a stale sort
    // that the BE query never received (header arrow would lie). See the
    // `urlControlledSorting` doc in use-table-persistence.ts.
    urlControlledSorting: !!onSortChange,
    isControlled,
    sanitizePersistedState,
  });

  // --- Density state (self-contained, localStorage-backed) ---
  type Density = "compact" | "default" | "comfortable";
  const densityKey = `${localStoragePrefix}-density`;
  const [density, setDensity] = React.useState<Density>("compact");
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(densityKey);
    if (stored === "compact" || stored === "default" || stored === "comfortable") {
      setDensity(stored);
    }
  }, [densityKey]);
  const cycleDensity = React.useCallback(() => {
    setDensity((prev) => {
      const next: Density =
        prev === "compact" ? "default" : prev === "default" ? "comfortable" : "compact";
      if (typeof window !== "undefined") localStorage.setItem(densityKey, next);
      return next;
    });
  }, [densityKey]);
  // Every density enforces both `tr` height AND `td` padding so rows render
  // at a uniform height regardless of cell content. Without this default, a
  // long wrapping title in one cell would push the row taller than its
  // siblings (B5 / 2026-05-06 phishing-campaigns audit).
  const densityClass =
    density === "compact"
      ? "[&_tbody_tr]:h-8 [&_td]:py-1 [&_td]:px-2"
      : density === "comfortable"
        ? "[&_tbody_tr]:h-16 [&_td]:py-3 [&_td]:px-4"
        : "[&_tbody_tr]:h-14 [&_td]:py-2 [&_td]:px-3";

  // --- Reconcile new filters into persisted state ---
  // Both column-bound filters (`filters`) and custom filters (`customFilters`)
  // share the same visibility/order namespace so they can be toggled +
  // reordered together in the Filters dropdown.
  React.useEffect(() => {
    if (
      (!filters && !customFilters && !dateFilters) ||
      !persistence.mounted
    )
      return;
    // Build the canonical declared order: column-bound filters first, then
    // custom filters, then date filters (matching the toolbar render order
    // below). Each entry carries enough info to derive its default
    // visibility. Date filters get defaultVisible=true so existing
    // operators see no behavior change on first encounter — they CAN
    // toggle them off via the Filters dropdown going forward.
    const declared: { id: string; defaultVisible: boolean }[] = [
      ...(filters ?? []).map((f) => ({
        id: f.id,
        defaultVisible:
          filterDefaults?.visibility?.[f.id] ?? f.defaultVisible ?? false,
      })),
      ...(customFilters ?? []).map((f) => ({
        id: f.id,
        defaultVisible:
          filterDefaults?.visibility?.[f.id] ?? f.defaultVisible ?? true,
      })),
      ...(dateFilters ?? []).map((d) => ({
        id: d.key,
        defaultVisible: filterDefaults?.visibility?.[d.key] ?? true,
      })),
    ];
    const allIds = declared.map((d) => d.id);
    const missingFromOrder = allIds.filter(
      (id) => !persistence.filterOrder.includes(id),
    );
    if (missingFromOrder.length > 0) {
      // Insert new filters at their declared position (based on declared order)
      const newOrder = [...persistence.filterOrder];
      for (const id of missingFromOrder) {
        const declaredIndex = allIds.indexOf(id);
        // Find the right insertion point relative to existing items
        let insertAt = newOrder.length;
        for (let i = declaredIndex - 1; i >= 0; i--) {
          const prevId = allIds[i];
          const existingIdx = newOrder.indexOf(prevId);
          if (existingIdx !== -1) {
            insertAt = existingIdx + 1;
            break;
          }
        }
        newOrder.splice(insertAt, 0, id);
      }
      persistence.setFilterOrder(newOrder);
      // Set visibility for new filters based on their defaultVisible or filterDefaults
      const visUpdates: Record<string, boolean> = {};
      for (const id of missingFromOrder) {
        const entry = declared.find((d) => d.id === id);
        visUpdates[id] = entry?.defaultVisible ?? false;
      }
      persistence.setFilterVisibility((prev) => ({ ...prev, ...visUpdates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, customFilters, dateFilters, persistence.mounted, persistence.filterOrder]);

  // --- Row Selection (controlled vs uncontrolled) ---
  const rowSelection = isControlled
    ? externalRowSelection
    : persistence.internalRowSelection;
  const setRowSelection = React.useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState),
    ) => {
      const newSelection =
        typeof updaterOrValue === "function"
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      if (isControlled) {
        onExternalRowSelectionChange?.(newSelection);
      } else {
        persistence.setInternalRowSelection(newSelection);
      }
    },
    [isControlled, rowSelection, onExternalRowSelectionChange, persistence],
  );

  const clearSelection = React.useCallback(() => {
    setRowSelection({});
    setIsAllAcrossPagesSelected(false);
    // setIsAllAcrossPagesSelected is declared AFTER this useCallback in the
    // component body (the controlled-vs-uncontrolled wiring needs filteredRowCount
    // first). Listing it in deps would compile to a TDZ access on the const at
    // line ~524 — Next.js production build fails with
    // "Cannot access 'setIsAllAcrossPagesSelected' before initialization".
    // It's a useCallback that's stable across renders anyway, so the dep is
    // semantically a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRowSelection]);

  // --- Search debounce (500ms) ---
  // CONTROLLED search: when the parent passes `searchValue` it owns the term
  // (typically in the URL), so the URL is the single source of truth. The input
  // then reads from `searchValue`, and typing does NOT persist the term to
  // localStorage — this is what fixes the cross-navigation desync where the box
  // showed a stale localStorage term that no longer matched the URL filter.
  const isControlledSearch = searchValue !== undefined;
  const [searchInputValue, setSearchInputValue] = React.useState(
    isControlledSearch ? searchValue : (persistence.globalFilter ?? ""),
  );
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchInputValue(value);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        // In controlled mode the URL is the source of truth — don't shadow the
        // term in localStorage (that copy is what desyncs across navigation).
        if (!isControlledSearch) persistence.setGlobalFilter(value);
        // Server-paginated callers (isServerPaginated) rely on this to
        // route the search value to their parent server query. See
        // UnifiedDataTableProps.onSearchChange JSDoc — without it,
        // server-paginated tables silently swallow the input because
        // the table's own globalFilter is pinned to "" at line below.
        if (onSearchChange) onSearchChange(value);
      }, 500);
    },
    [persistence, onSearchChange, isControlledSearch],
  );

  // Mirror the EXTERNAL search source into the visible input:
  //   - controlled: the parent-owned `searchValue` (URL), so navigating back to
  //     a list WITHOUT `?search=` correctly clears the box (and a restored
  //     `?search=` re-fills it) — box and applied filter always match,
  //   - uncontrolled: the persisted `globalFilter` (mount-time load of a saved
  //     term so the box shows what is actually filtering the list; prod
  //     2026-06-26: a persisted "Search groups" term filtered to empty while the
  //     input looked blank, reading as "no groups exist"),
  //   - the X / "Clear filters" button setting it back to "",
  //   - a parent-driven programmatic set.
  // The user's own typing is owned by the 500ms debounce and is never echoed
  // back here for the uncontrolled path: `persistence.globalFilter` only changes
  // AFTER the debounce flushes, by which point it already equals
  // `searchInputValue`, so this effect no-ops for in-flight keystrokes.
  const externalSearch = isControlledSearch
    ? searchValue
    : (persistence.globalFilter ?? "");
  React.useEffect(() => {
    if (externalSearch !== searchInputValue) {
      setSearchInputValue(externalSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSearch]);

  // --- Advanced Search ---
  const [advancedSearchOpen, setAdvancedSearchOpen] = React.useState(false);
  // Controlled mode wins when the consumer supplies BOTH state + setter
  // (see AdvancedSearchConfig.state). This lets parent pages clear the
  // filter externally and the trigger badge stays in sync — internal
  // persistence in `useTablePersistence` would otherwise hold a stale copy
  // because nothing tells it to drop the in-memory value when localStorage
  // is cleared from outside the table.
  const isAdvancedSearchControlled =
    advancedSearch?.state !== undefined &&
    advancedSearch?.onStateChange !== undefined;
  // Persisted state is untyped-at-load (deserialized JSON) — narrow to the
  // consumer's TSearchState at the component boundary. Consumer owns the shape.
  const advancedSearchState = (
    isAdvancedSearchControlled
      ? (advancedSearch!.state ?? advancedSearch!.emptyState)
      : (persistence.advancedSearchState ?? advancedSearch?.emptyState)
  ) as TSearchState | undefined;
  const setAdvancedSearchState = (
    isAdvancedSearchControlled
      ? advancedSearch!.onStateChange!
      : persistence.setAdvancedSearchState
  ) as (next: TSearchState) => void;

  const filteredData = React.useMemo(() => {
    if (!advancedSearch || !advancedSearchState) return data;
    return advancedSearch.applySearch(data, advancedSearchState);
  }, [data, advancedSearch, advancedSearchState]);

  const activeAdvancedSearchCount = React.useMemo(() => {
    if (!advancedSearch || !advancedSearchState) return 0;
    const conditions = (advancedSearchState as { conditions?: unknown }).conditions;
    if (!Array.isArray(conditions)) return 0;
    return (conditions as { operator?: string; value?: string }[]).filter(
      (c) =>
        c.operator === "is_empty" ||
        c.operator === "is_not_empty" ||
        (c.value && c.value.trim() !== ""),
    ).length;
  }, [advancedSearch, advancedSearchState]);

  // --- DnD Sensors (standardized: 8px activation distance) ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // --- Bulk Action Confirmation Dialog ---
  const [confirmAction, setConfirmAction] =
    React.useState<BulkActionConfig<TData> | null>(null);

  // --- React Table ---
  // When server-side pagination is active, the server handles ALL filtering.
  // Disable client-side getFilteredRowModel and globalFilter to prevent
  // double-filtering that causes rows to disappear.
  const isServerPaginated = !!pagination;

  // Wrap setSorting so we can fan out to the optional `onSortChange` prop.
  // Server-paginated callers wire their server-query sort key to this so
  // header-chevron clicks actually reach the BE (not just localStorage).
  const handleSortingChange = React.useCallback(
    (
      updaterOrValue:
        | import("@tanstack/react-table").SortingState
        | ((
            old: import("@tanstack/react-table").SortingState,
          ) => import("@tanstack/react-table").SortingState),
    ) => {
      const next =
        typeof updaterOrValue === "function"
          ? (
              updaterOrValue as (
                old: import("@tanstack/react-table").SortingState,
              ) => import("@tanstack/react-table").SortingState
            )(persistence.sorting)
          : updaterOrValue;
      persistence.setSorting(next);
      if (onSortChange) {
        const first = next[0];
        onSortChange(first ? { id: first.id, desc: first.desc } : null);
      }
    },
    [persistence, onSortChange],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableRowSelection,
    // Server-controlled tables (those that wire `onSortChange`) must never
    // drop to an unsorted state via a header click. With the default
    // `enableSortingRemoval: true`, TanStack's cycle is none→asc→desc→none,
    // so a column seeded via `defaultSorting` (which starts mid-cycle at
    // `desc`) jumps straight to "none" on the first click — `onSortChange`
    // fires `null`, the parent snaps back to its default, and the header
    // chevron and the BE query desync (prod "status DESC not working"
    // 2026-05-17). Collapsing the cycle to a clean desc⇄asc toggle keeps the
    // table sort state and the parent server-query state in lockstep.
    enableSortingRemoval: onSortChange ? false : undefined,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: (updater) => {
      persistence.setColumnFilters(updater);
      // Server-side filter wiring: read the resolved next state and bubble
      // it up so server-paginated tables can update their query params.
      // Without this, filter chip clicks only mutate localStorage and the
      // BE never refetches with the new filter (audit-log tickets
      // 28af9f12 + 0cfc22f9).
      if (onFilterChange) {
        const next =
          typeof updater === 'function'
            ? updater(persistence.columnFilters)
            : updater;
        onFilterChange(next);
      }
    },
    onColumnSizingChange: persistence.setColumnSizing,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: persistence.setGlobalFilter,
    ...(isServerPaginated ? {} : { globalFilterFn }),
    getCoreRowModel: getCoreRowModel(),
    // When the caller is server-paginated AND wired its server-sort query
    // param via `onSortChange`, sorting is the SERVER's job — `manualSorting`
    // tells TanStack to leave row order untouched and just report header
    // clicks. Without this, `getSortedRowModel()` re-sorts only the current
    // page client-side, so a status sort "works" only when every row fits on
    // one page (prod report 2026-05-16). Server-paginated tables that did NOT
    // wire `onSortChange` keep client sorting (better than no sort at all).
    manualSorting: isServerPaginated && !!onSortChange,
    getSortedRowModel: getSortedRowModel(),
    ...(isServerPaginated ? {} : { getFilteredRowModel: getFilteredRowModel() }),
    onColumnVisibilityChange: persistence.setColumnVisibility,
    onColumnOrderChange: persistence.setColumnOrder,
    ...(getRowId ? { getRowId: getRowId as (row: TData) => string } : {}),
    // Pagination: external control when pagination prop provided, otherwise show all
    ...(pagination
      ? {
          manualPagination: true,
          pageCount: pagination.totalPages,
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: (
            updater:
              | ((prev: { pageIndex: number; pageSize: number }) => {
                  pageIndex: number;
                  pageSize: number;
                })
              | { pageIndex: number; pageSize: number },
          ) => {
            const current = {
              pageIndex: pagination.page - 1,
              pageSize: pagination.pageSize,
            };
            const next =
              typeof updater === "function" ? updater(current) : updater;
            if (next.pageIndex !== current.pageIndex) {
              pagination.onPageChange(next.pageIndex + 1);
            }
            if (next.pageSize !== current.pageSize) {
              pagination.onPageSizeChange(next.pageSize);
            }
          },
        }
      : {
          // No pagination prop — render all data, no pagination controls
          getPaginationRowModel: getPaginationRowModel(),
        }),
    defaultColumn: {
      size: 150,
      minSize: 80,
      maxSize: 500,
    },
    initialState: {
      columnPinning: {
        left: columnDefaults?.pinned?.left ?? [],
        right: columnDefaults?.pinned?.right ?? [],
      },
      ...(pagination
        ? {}
        : clientPagination
          ? { pagination: { pageSize: clientPagination.pageSize ?? 25 } }
          : { pagination: { pageSize: 9999 } }),
    },
    state: {
      sorting: persistence.sorting,
      // Always pass columnFilters for UI display (filter chips). Client-side filtering
      // is disabled via getFilteredRowModel being absent when server-paginated.
      columnFilters: persistence.columnFilters,
      columnVisibility: persistence.columnVisibility,
      columnOrder: persistence.columnOrder,
      columnSizing: persistence.columnSizing,
      rowSelection,
      globalFilter: isServerPaginated ? "" : persistence.globalFilter,
      ...(pagination && {
        pagination: {
          pageIndex: pagination.page - 1,
          pageSize: pagination.pageSize,
        },
      }),
    },
    meta: tableMeta,
  });

  // Prune selection ids that are no longer in the data. After a PARTIAL bulk
  // action (e.g. Release 5 of 10, 5 remain queued) the removed rows leave the
  // data on refetch, but TanStack keeps their rowSelection entries — so the raw
  // selected-count (and the bulk bar) would stay stale until manually cleared
  // (#1791). Client-side only; server pages can't enumerate every id.
  React.useEffect(() => {
    if (isServerPaginated) return;
    const present = new Set(table.getCoreRowModel().rows.map((r) => r.id));
    setRowSelection((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      let changed = false;
      const next: RowSelectionState = {};
      for (const id of keys) {
        if (present.has(id)) next[id] = prev[id];
        else changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Expose the client-side FILTERED rows so a page can compute its summary/KPI
  // cards from the SAME subset the table shows (opt-in via onFilteredRowsChange;
  // no-op for server-paginated tables — they filter on the server, and calling
  // getFilteredRowModel() there is unconfigured). RENDER-LOOP SAFE: the effect
  // is keyed on a signature of the filtered row ids, NOT the rows array
  // identity, so a parent that stores the emitted rows in state does not re-fire
  // this effect (same filtered set → same signature). The callback is read via
  // a ref so its identity is not a dependency.
  const onFilteredRowsChangeRef = React.useRef(onFilteredRowsChange);
  onFilteredRowsChangeRef.current = onFilteredRowsChange;
  const filteredRowsSignature =
    isServerPaginated || !onFilteredRowsChange
      ? null
      : table.getFilteredRowModel().rows.map((r) => r.id).join(",");
  React.useEffect(() => {
    if (isServerPaginated || !onFilteredRowsChangeRef.current) return;
    onFilteredRowsChangeRef.current(
      table.getFilteredRowModel().rows.map((r) => r.original),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRowsSignature, isServerPaginated]);

  // Expose the APPLIED FILTER STATE (column-filter chip values + global search
  // term) so a page can stamp an export's provenance banner with exactly the
  // filters the operator has on screen (LANE A4). Opt-in; render-loop safe via a
  // JSON signature; callback read through a ref so its identity is not a dep.
  const onFilterStateChangeRef = React.useRef(onFilterStateChange);
  onFilterStateChangeRef.current = onFilterStateChange;
  const filterStateSignature = onFilterStateChange
    ? JSON.stringify({
        c: persistence.columnFilters,
        g: persistence.globalFilter ?? "",
      })
    : null;
  React.useEffect(() => {
    if (!onFilterStateChangeRef.current) return;
    onFilterStateChangeRef.current({
      columnFilters: persistence.columnFilters as {
        id: string;
        value: unknown;
      }[],
      globalFilter: persistence.globalFilter ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStateSignature]);

  const rawSelectedRowCount = Object.keys(rowSelection).length;
  const selectedItems = table
    .getFilteredSelectedRowModel()
    .rows.map((r) => r.original);

  // --- Select All Across Pages (Gmail-style) ---
  // When server-paginated, use the server's total count (not the client-side filtered count)
  const filteredRowCount = isServerPaginated
    ? (pagination?.total ?? data.length)
    : table.getFilteredRowModel().rows.length;
  const pageRowCount = table.getRowModel().rows.length;
  const allPageRowsSelected =
    pageRowCount > 0 && table.getIsAllPageRowsSelected();

  const totalColWidth = table.getCenterTotalSize();

  // --- Proportional column widths ("magic" CSS-only distribution) ---
  // Columns fill 100% of the container and share the width PROPORTIONALLY to
  // their defined `size`, so the first/large columns (Name) get more room and
  // narrow ones (Status badges) get less — while truly fixed columns (select,
  // actions, anything with enableResizing === false) keep an exact px width.
  // Flexible columns get a `width: N%` of the flexible budget; their `size`
  // becomes a `min-width` so the table scrolls horizontally instead of
  // crushing them when the viewport is narrow. Stable across populated/empty
  // states (no row-count-dependent slack redistribution → no shrink/snap),
  // and no filler column or width-measuring needed.
  const colWidthMeta = React.useMemo(() => {
    const leaf = table.getVisibleLeafColumns();
    const isFixed = (c: (typeof leaf)[number]) =>
      c.columnDef.enableResizing === false ||
      // pinned utility columns are always fixed
      c.id === "select" ||
      c.id === "actions";
    const flexCols = leaf.filter((c) => !isFixed(c));
    const flexSizeSum = flexCols.reduce((s, c) => s + (c.getSize() || 0), 0) || 1;
    const styleFor = (colId: string): React.CSSProperties => {
      const col = leaf.find((c) => c.id === colId);
      if (!col) return {};
      const size = col.getSize();
      if (isFixed(col)) {
        return { width: `${size}px`, minWidth: `${size}px`, maxWidth: `${size}px` };
      }
      const pct = (size / flexSizeSum) * 100;
      return {
        width: `${pct}%`,
        minWidth: `${col.columnDef.minSize || size}px`,
      };
    };
    return { styleFor };
  }, [table, persistence.columnSizing, persistence.columnVisibility]);

  // Synthetic "all across pages selected" flag — Gmail-style.
  // When server-paginated, we can't enumerate every row ID into rowSelection
  // without fetching every page. Instead, we flip this boolean and have bulk
  // actions send { selectAll: true, filters: activeFilters } to the backend,
  // which already supports that mode via BulkTargetBase.
  //
  // Controlled mode: when the parent owns the flag (passed via
  // externalAllAcrossPagesSelected), the table reflects parent state instead
  // of managing it locally. This keeps the synthetic flag alive across
  // remounts (wizard step navigation, route change, etc.) — same pattern
  // as externalRowSelection.
  const isAllAcrossPagesControlled =
    externalAllAcrossPagesSelected !== undefined;
  const [internalAllAcrossPagesSelected, setInternalAllAcrossPagesSelected] =
    React.useState(false);
  const isAllAcrossPagesSelected = isAllAcrossPagesControlled
    ? externalAllAcrossPagesSelected
    : internalAllAcrossPagesSelected;
  const setIsAllAcrossPagesSelected = React.useCallback(
    (value: boolean) => {
      if (isAllAcrossPagesControlled) {
        onExternalAllAcrossPagesSelectedChange?.(value);
        return;
      }
      setInternalAllAcrossPagesSelected(value);
    },
    [isAllAcrossPagesControlled, onExternalAllAcrossPagesSelectedChange],
  );

  // Bug #53 — when the synthetic "all across pages" flag is on (or the
  // client-side equivalent — every filtered row is selected), the footer
  // should report the true filtered total instead of the page-bounded
  // rowSelection keycount. The select-all banner and footer must agree or
  // users get conflicting signals ("All 5,000 selected" / "25 selected").
  const selectedRowCount = isAllAcrossPagesSelected
    ? filteredRowCount
    : rawSelectedRowCount;

  // When the filter / page changes (pagination?.page change, columnFilters
  // change, or rowSelection manually edited) the synthetic flag is stale.
  // Reset it whenever the selection becomes incomplete.
  //
  // CONTROLLED MODE: the parent owns the synthetic flag's lifecycle. It
  // also owns the per-page selection diff (excludeRecipientIds). When the
  // operator unchecks ONE row inside selectAll mode, the parent adds that
  // id to excludeRecipientIds while keeping selectAll=true (intentional —
  // selectAll-minus-N semantics). At that moment getIsAllPageRowsSelected
  // briefly returns false, but the parent's selectAll intent is unchanged.
  // The previous auto-reset would fire here and notify the parent
  // `setSelectAll(false)`, collapsing selectedRecipientIds (which was [] by
  // selectAll-mode invariant) to zero — the exact regression a 2026-05-22
  // critique pass caught. In controlled mode the parent IS the source of
  // truth; the table never writes the flag back via this effect.
  React.useEffect(() => {
    if (isAllAcrossPagesControlled) return;
    if (!allPageRowsSelected) setIsAllAcrossPagesSelected(false);
  }, [
    allPageRowsSelected,
    pagination?.page,
    persistence.columnFilters,
    isAllAcrossPagesControlled,
    setIsAllAcrossPagesSelected,
  ]);

  // Client-paginated case: treat "all filtered rows present in rowSelection"
  // as equivalent to the synthetic flag.
  const clientAllFilteredRowsSelected =
    !isServerPaginated &&
    filteredRowCount > 0 &&
    selectedRowCount === filteredRowCount;
  const allFilteredRowsSelected =
    isAllAcrossPagesSelected || clientAllFilteredRowsSelected;
  const showSelectAllBanner =
    enableRowSelection &&
    allPageRowsSelected &&
    !allFilteredRowsSelected &&
    filteredRowCount > pageRowCount;
  const showAllSelectedBanner =
    enableRowSelection &&
    allFilteredRowsSelected &&
    filteredRowCount > pageRowCount;

  const selectAllFiltered = React.useCallback(() => {
    if (isServerPaginated) {
      // Just flip the synthetic flag. No fetch, no row enumeration.
      // The bulk action toolbar will pick this up via the `meta` arg and
      // dispatch filter-based bulk operations to the backend.
      setIsAllAcrossPagesSelected(true);
      return;
    }
    // Client-paginated: the full filtered set is already in memory, so
    // materialising rowSelection is cheap and correct.
    const newSelection: RowSelectionState = {};
    for (const row of table.getFilteredRowModel().rows) {
      newSelection[row.id] = true;
    }
    setRowSelection(newSelection);
  }, [isServerPaginated, table, setRowSelection, setIsAllAcrossPagesSelected]);

  // --- Column Drag End ---
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const allPinned = [...persistence.pinnedLeft, ...persistence.pinnedRight];
    const ids = table
      .getAllLeafColumns()
      .filter((c) => !allPinned.includes(c.id))
      .map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(ids, oldIndex, newIndex);
    persistence.setColumnOrder([
      ...persistence.pinnedLeft,
      ...reordered,
      ...persistence.pinnedRight,
    ]);
  };

  // --- Filter Drag End ---
  const handleFilterDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = persistence.filterOrder.findIndex(
        (id) => id === active.id,
      );
      const newIndex = persistence.filterOrder.findIndex(
        (id) => id === over?.id,
      );
      if (oldIndex !== -1 && newIndex !== -1) {
        persistence.setFilterOrder(
          arrayMove(persistence.filterOrder, oldIndex, newIndex),
        );
      }
    }
  };

  // --- Header Drag-and-Drop (direct on table headers) ---
  const [activeHeaderId, setActiveHeaderId] = React.useState<string | null>(
    null,
  );

  const handleHeaderDragStart = (event: DragStartEvent) => {
    setActiveHeaderId(String(event.active.id));
  };

  const handleHeaderDragEnd = (event: DragEndEvent) => {
    setActiveHeaderId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const pinned = [...persistence.pinnedLeft, ...persistence.pinnedRight];
    const ids = table
      .getAllLeafColumns()
      .filter((c) => !pinned.includes(c.id))
      .map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(ids, oldIndex, newIndex);
    persistence.setColumnOrder([
      ...persistence.pinnedLeft,
      ...reordered,
      ...persistence.pinnedRight,
    ]);
  };

  const handleHeaderDragCancel = () => {
    setActiveHeaderId(null);
  };

  // --- Column display name helper ---
  const getDisplayName = (columnId: string): string => {
    return (
      columnDisplayNames[columnId] ||
      columnId.charAt(0).toUpperCase() + columnId.slice(1)
    );
  };

  // --- Draggable columns (exclude pinned) ---
  const allPinned = [...persistence.pinnedLeft, ...persistence.pinnedRight];
  const draggableColumns = table
    .getAllLeafColumns()
    .filter((c) => !allPinned.includes(c.id));
  const draggableColumnIds = draggableColumns.map((c) => c.id);

  // --- Lookup map for custom filter configs (id -> render fn) ---
  const customFilterMap = React.useMemo(() => {
    const map = new Map<string, CustomFilterConfig>();
    for (const cf of customFilters ?? []) map.set(cf.id, cf);
    return map;
  }, [customFilters]);

  // --- Has any filter chips visible (column-bound OR custom) ---
  const hasVisibleFilters =
    (filters || customFilters) &&
    persistence.filterOrder.some((id) => persistence.filterVisibility[id]);

  // --- Active filter/search detection (for the filtered-empty no-results
  //     state — "data exists but the current filters match nothing"). ---
  const hasActiveTableFilters =
    persistence.columnFilters.length > 0 ||
    (searchInputValue?.trim().length ?? 0) > 0 ||
    // Include the PERSISTED global filter, not just the live input. On a fresh
    // mount the persistence hook loads the persisted search term into
    // `persistence.globalFilter` AFTER the initial render, while
    // `searchInputValue` may still be "" — so a reloaded search must be
    // detected here too, otherwise a filtered-to-empty list is mistaken for a
    // truly-empty one (prod 2026-06-26: a persisted "Search groups" term hid a
    // real group behind the "Create your first group" empty state).
    (persistence.globalFilter?.trim().length ?? 0) > 0 ||
    activeAdvancedSearchCount > 0 ||
    customFiltersActive === true;

  // Reset the in-table filters + search + advanced search so the operator can
  // recover from a filtered-empty result. Server-paginated callers also get
  // their onSearchChange("") + onFilterChange([]) pinged so the server query
  // re-widens.
  const clearAllTableFilters = React.useCallback(() => {
    persistence.setColumnFilters([]);
    persistence.setGlobalFilter("");
    setSearchInputValue("");
    if (onSearchChange) onSearchChange("");
    if (onFilterChange) onFilterChange([]);
    // Reset consumer-owned custom filters (e.g. the phishing Activity report's
    // Bot/Honeypot/Transport/IP dropdowns) too — otherwise a table filtered
    // only via custom chips stays stuck (no built-in filter to clear).
    onClearCustomFilters?.();
    // Advanced search has no top-level clear; reset via its controlled
    // onStateChange(emptyState) when the consumer owns the state.
    if (advancedSearch?.onStateChange) {
      advancedSearch.onStateChange(advancedSearch.emptyState);
    }
    try {
      localStorage.removeItem(`${localStoragePrefix}-column-filters`);
    } catch {
      /* ignore */
    }
  }, [persistence, onSearchChange, onFilterChange, advancedSearch, localStoragePrefix]);

  // --- Empty State: "replace" mode ---
  // Rendered INSIDE the baked card so an empty table still shows the framed,
  // full-height card (not bare content on the page background). The content is
  // centered vertically + horizontally within that card.
  //
  // CRITICAL: only do the full-table replace when the list is GENUINELY empty
  // (no search/filter active). When a search or filter is what emptied the
  // list, the full replace would hide the toolbar+search and falsely claim the
  // data doesn't exist ("Create your first group") — the exact prod bug on
  // 2026-06-26 where a persisted "Search groups" term made a real group look
  // missing. With filters active we fall through to the normal render, which
  // keeps the search box visible and shows the "No results match your filters
  // — Clear filters" state (see the no-results overlay below).
  if (
    emptyState?.mode === "replace" &&
    data.length === 0 &&
    !hasActiveTableFilters &&
    !isFetching
  ) {
    return (
      <div
        className={cn(
          "w-full flex flex-col",
          // Match the <Card> component chrome so a UDT-with-card and a
          // page-level <Card>-wrapping a UDT (card={false}) render the
          // same: rounded-xl + subtle shadow (2026-06-17 design alignment).
          card && "rounded-xl border bg-card shadow-sm overflow-hidden",
          card && (fillHeight ? "flex-1 min-h-[600px]" : "min-h-[600px]"),
          // Embedded (card=false): still give the empty state a real floor so
          // it centers instead of collapsing into a stray right-aligned row.
          !card && (fillHeight ? "flex-1 min-h-[300px]" : "min-h-[300px]"),
        )}
      >
        <div className="flex flex-1 flex-col items-center justify-center py-16 px-4 text-center">
          {emptyState.icon}
          <h3 className="text-lg font-semibold mt-4">{emptyState.title}</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md">
            {emptyState.description}
          </p>
          {emptyState.actions && <div className="mt-6">{emptyState.actions}</div>}
        </div>
      </div>
    );
  }

  // --- Render ---
  return (
    <div
      className={cn(
        "w-full",
        // Baked-in card chrome — single source of truth for table framing.
        // Callers must NOT add their own <Card> wrapper. The card always
        // gives border + bg + a flex column. Height behaviour:
        //  • fillHeight → flex-1 to GROW into the available height of a sized
        //    flex-column parent, with a 450px FLOOR so it never collapses on a
        //    short/empty page. The inner scroll body (min-h-0) absorbs the
        //    growth and scrolls internally.
        //  • otherwise → fixed min-h-[600px] (legacy, non-filling callers).
        // Match the <Card> component chrome (rounded-xl + shadow-sm) so all
        // tables look the same whether they use UDT's built-in card or get
        // wrapped in a page-level <Card> (card={false}). 2026-06-17.
        card && "rounded-xl border bg-card shadow-sm flex flex-col overflow-hidden",
        card && (fillHeight ? "flex-1 min-h-[600px]" : "min-h-[600px]"),
        // Non-card fillHeight (embedded/legacy) keeps its original behaviour.
        !card && fillHeight && "flex h-full flex-col min-h-0",
      )}
    >
      {/* Toolbar — px-4 keeps horizontal breathing room; py-2 trims the
          top/bottom padding so the search row sits closer to the table
          header (2026-05-06 audit). */}
      <div className="flex flex-col gap-2 p-4 border-b">
        {/* Row 1: Search + actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {renderToolbarLeft?.(table)}

            {/* Search */}
            {enableSearch && (
              <div className="flex flex-col gap-0.5">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchInputValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="h-9 w-50 lg:w-70 pl-8 pr-8"
                  />
                  {searchInputValue && (
                    <button
                      type="button"
                      title="Clear search"
                      onClick={() => {
                        setSearchInputValue("");
                        persistence.setGlobalFilter("");
                        if (onSearchChange) onSearchChange("");
                      }}
                      className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {searchHintText && (
                  <p className="text-xs text-muted-foreground pl-1">{searchHintText}</p>
                )}
              </div>
            )}

            {/* Advanced search trigger */}
            {advancedSearch?.renderTrigger({
              onClick: () => setAdvancedSearchOpen(true),
              activeCount: activeAdvancedSearchCount,
            })}

            {/* Filter chips — ALWAYS inline with search (the row is flex-wrap
                so they flow onto a new line only when space runs out, saving
                vertical space). The legacy `below` placement now renders here
                too; the separate Row 2 below is disabled. */}
            {hasVisibleFilters && (
              <>
                {persistence.filterOrder.map((filterId) => {
                  const custom = customFilterMap.get(filterId);
                  // A custom filter that currently HOLDS a value force-shows in
                  // the toolbar even if the user hid it — so an active filter
                  // (e.g. a stat-card-applied metric) stays visible and
                  // understandable. Inactive filters still respect the toggle.
                  const forceShow = custom?.active === true;
                  if (!persistence.filterVisibility[filterId] && !forceShow) {
                    return null;
                  }
                  const colConfig = filters?.find((f) => f.id === filterId);
                  if (colConfig) {
                    return (
                      <MultiSelectFilter
                        key={filterId}
                        title={colConfig.title}
                        options={colConfig.options}
                        selectedValues={
                          (table
                            .getColumn(colConfig.column)
                            ?.getFilterValue() as string[]) ?? []
                        }
                        onSelectionChange={(values) =>
                          table
                            .getColumn(colConfig.column)
                            ?.setFilterValue(
                              values.length > 0 ? values : undefined,
                            )
                        }
                        hideSearchMode={colConfig.hideSearchMode}
                      />
                    );
                  }
                  if (custom) {
                    return (
                      <React.Fragment key={filterId}>
                        {custom.render()}
                      </React.Fragment>
                    );
                  }
                  return null;
                })}
              </>
            )}

            {/* Independent date-range filter chips (inline placement).
                Each chip is its own compact popover; consumer owns state.
                Gated by persistence.filterVisibility[df.key] so the
                Filters dropdown can toggle them on/off like every other
                filter. */}
            {dateFilters?.map((df) =>
                persistence.filterVisibility[df.key] === false ? null : (
                  <DateFieldFilter
                    key={df.key}
                    label={df.label}
                    value={df.value}
                    onChange={df.onChange}
                    presets={df.presets}
                    disableFutureDates={df.disableFutureDates}
                  />
                ),
              )}

            {/* Clear all — placed AFTER every chip (regular + date) so it
                consistently sits at the end of the chip flow regardless of
                wrap order. Previously rendered between the regular and date
                chips, which made it appear "sandwiched" in the middle of the
                toolbar (P2 placement fix 2026-06-12).

                Clears search input + global filter + column filters + fires
                onSearchChange("") + onFilterChange([]) so server-paginated
                tables also re-widen, not just the visible chips.

                Gated on ANY active filter (column, global filter, or
                search) — so it now appears even when the user has typed a
                search query but hasn't picked a chip yet, which is the
                more common "I want to undo my filtering" case. */}
            {(persistence.columnFilters.length > 0 ||
              persistence.globalFilter ||
              searchInputValue ||
              customFiltersActive) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={clearAllTableFilters}
              >
                <X className="h-3 w-3 mr-1" />
                Clear all
              </Button>
            )}

            {renderToolbarCenter?.(table)}
          </div>

          <div className="flex items-center gap-2">
            {renderToolbarRight?.(table)}

            {/* Density toggle — cycles compact ↔ default ↔ comfortable.
                Persists in localStorage. Tooltip shows the current mode. */}
            {enableDensityToggle && (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                onClick={cycleDensity}
                title={`Row density: ${density} — click to cycle`}
                aria-label={`Row density: ${density} — click to cycle`}
              >
                <Rows3 className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}

            {/* Saved Views menu (opt-in via savedViewsKey).
                Snapshots the full persisted-state bundle and re-applies it
                on selection. Pure FE — localStorage namespace
                `<savedViewsKey>:saved-views`. */}
            {savedViewsKey && (
              <SavedViewsMenu
                storageKey={savedViewsKey}
                currentView={{
                  columnVisibility: persistence.columnVisibility,
                  columnOrder: persistence.columnOrder,
                  columnSizing: persistence.columnSizing,
                  sorting: persistence.sorting,
                  columnFilters: persistence.columnFilters,
                  globalFilter: persistence.globalFilter,
                  filterVisibility: persistence.filterVisibility,
                  filterOrder: persistence.filterOrder,
                }}
                onApplyView={(v: ViewState) => {
                  if (v.columnVisibility)
                    persistence.setColumnVisibility(v.columnVisibility);
                  if (v.columnOrder) persistence.setColumnOrder(v.columnOrder);
                  if (v.columnSizing) persistence.setColumnSizing(v.columnSizing);
                  if (v.sorting) persistence.setSorting(v.sorting);
                  if (v.columnFilters)
                    persistence.setColumnFilters(v.columnFilters);
                  if (typeof v.globalFilter === "string")
                    persistence.setGlobalFilter(v.globalFilter);
                  if (v.filterVisibility)
                    persistence.setFilterVisibility(v.filterVisibility);
                  if (v.filterOrder) persistence.setFilterOrder(v.filterOrder);
                }}
              />
            )}

            {/* Columns Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Toggle columns">
                  <Columns3 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-55">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-xs font-semibold text-muted-foreground">
                    Columns
                  </span>
                  <button
                    onClick={persistence.resetColumns}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    <RotateCcw className="h-2.5 w-2.5" />
                    Reset
                  </button>
                </div>
                {/* FIX R1-m19 — drag affordance hint */}
                <p className="text-[10px] text-muted-foreground px-2 pb-1.5">
                  Drag rows to reorder columns
                </p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={draggableColumnIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {draggableColumns.map((column) => (
                      <SortableColumnItem
                        key={column.id}
                        column={column}
                        displayName={getDisplayName(column.id)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-xs text-muted-foreground justify-center gap-1.5"
                  onSelect={persistence.resetColumns}
                >
                  <RotateCcw className="h-3 w-3" />
                  Reset to defaults
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Filters Dropdown — lists both column-bound + custom filters */}
            {((filters && filters.length > 0) ||
              (customFilters && customFilters.length > 0)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Customize filters">
                    <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-45">
                  {/* FIX R1-m20 — "Active filters: X of Y" summary */}
                  {(() => {
                    const totalFilterCount =
                      (filters?.length ?? 0) +
                      (customFilters?.length ?? 0) +
                      (dateFilters?.length ?? 0)
                    const activeFilterCount = persistence.filterOrder.filter(id => persistence.filterVisibility[id]).length
                    return (
                      <div className="px-2 py-1.5 flex items-center justify-between">
                        <span className="text-xs font-semibold text-muted-foreground">Filters</span>
                        {totalFilterCount > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            {activeFilterCount} of {totalFilterCount} active
                          </span>
                        )}
                      </div>
                    )
                  })()}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleFilterDragEnd}
                  >
                    <SortableContext
                      items={persistence.filterOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      {persistence.filterOrder.map((filterId) => {
                        const colConfig = filters?.find(
                          (f) => f.id === filterId,
                        );
                        const custom = customFilterMap.get(filterId);
                        const dateConfig = dateFilters?.find(
                          (d) => d.key === filterId,
                        );
                        const displayName =
                          colConfig?.title ??
                          custom?.label ??
                          dateConfig?.label ??
                          null;
                        if (!displayName) return null;
                        return (
                          <SortableFilterItem
                            key={filterId}
                            filterId={filterId}
                            displayName={displayName}
                            checked={
                              persistence.filterVisibility[filterId] ?? false
                            }
                            onCheckedChange={(value) =>
                              persistence.setFilterVisibility((prev) => ({
                                ...prev,
                                [filterId]: !!value,
                              }))
                            }
                          />
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-muted-foreground justify-center gap-1.5"
                    onSelect={persistence.resetFilters}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset to defaults
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

      </div>

      {/* Bulk Actions */}
      {selectedRowCount > 0 &&
        renderBulkActions &&
        renderBulkActions(table, selectedItems, clearSelection, {
          allFilteredSelected: allFilteredRowsSelected,
          filteredTotal: filteredRowCount,
        })}
      {selectedRowCount > 0 &&
        !renderBulkActions &&
        bulkActions &&
        bulkActions.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b">
            <span className="text-sm text-muted-foreground">
              {selectedRowCount} row(s) selected
            </span>
            <div className="flex items-center gap-1 ml-2">
              {bulkActions.map((action) => (
                <Button
                  key={action.id}
                  variant={action.variant ?? "outline"}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    if (action.confirm) {
                      setConfirmAction(action);
                    } else {
                      action.onExecute(selectedItems, clearSelection, {
                        allFilteredSelected: allFilteredRowsSelected,
                        filteredTotal: filteredRowCount,
                      });
                    }
                  }}
                >
                  {action.icon}
                  <span className={action.icon ? "ml-1" : ""}>
                    {action.label}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )}

      {/* Select All Across Pages Banner (Gmail-style) */}
      {showSelectAllBanner && (
        <div className="flex items-center gap-1 px-4 py-2 bg-primary/5 border-b text-sm">
          <span className="text-muted-foreground animate-select-all-attention">
            All {pageRowCount} on this page are selected.
          </span>
          <button
            type="button"
            onClick={selectAllFiltered}
            className="text-primary underline underline-offset-2 font-semibold cursor-pointer hover:text-primary/80 animate-select-all-attention"
          >
            Select all {filteredRowCount} results
          </button>
        </div>
      )}
      {showAllSelectedBanner && (
        <div className="flex items-center gap-1 px-4 py-2 bg-primary/5 border-b text-sm">
          <span className="text-muted-foreground">
            All {filteredRowCount} results are selected.
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-primary underline underline-offset-2 font-semibold cursor-pointer hover:text-primary/80"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleHeaderDragStart}
        onDragEnd={handleHeaderDragEnd}
        onDragCancel={handleHeaderDragCancel}
      >
        <div
          className={cn(
            "overflow-auto relative",
            densityClass,
            // Grow the scroll body to fill the card (or the fillHeight parent)
            // so the footer pins to the bottom and short result sets don't
            // leave a collapsed, ragged table above empty card space.
            (fillHeight || card) && "flex-1 min-h-0",
          )}
          style={fillHeight || card ? undefined : { maxHeight, minHeight }}
        >
          {isFetching && (
            <div className="absolute inset-0 z-10 bg-background/50 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
              <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {/* Bare <table> (NOT the <Table> primitive) — the primitive wraps the
              table in its own `overflow-x-auto` container div, which becomes a
              nested scroll context that traps position:sticky and stops the
              header from freezing against THIS overflow-auto body. Rendering
              the table directly here keeps the sticky <th> anchored to the
              scrolling body so the header freezes on vertical scroll. */}
          <table
            data-slot="table"
            className="caption-bottom text-sm table-fixed"
            style={{
              // Always 100% — flexible columns take a % share (proportional to
              // their `size`) and fixed columns take px, so the table fills the
              // container with the first column widest and Actions narrow.
              // `minWidth` on the table = total min so it scrolls when too narrow.
              width: "100%",
              minWidth: `${totalColWidth}px`,
            }}
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                const visibleNonPinnedIds = headerGroup.headers
                  .filter((h) => !allPinned.includes(h.column.id))
                  .map((h) => h.column.id);

                return (
                  <SortableContext
                    key={headerGroup.id}
                    items={visibleNonPinnedIds}
                    strategy={horizontalListSortingStrategy}
                  >
                    <TableRow>
                      {headerGroup.headers.map((header) => {
                        const isPinned = allPinned.includes(header.column.id);

                        if (isPinned) {
                          const pinnedSortDir = header.column.getCanSort() ? header.column.getIsSorted() : undefined
                          const pinnedAriaSort: React.AriaAttributes["aria-sort"] =
                            pinnedSortDir === "asc"
                              ? "ascending"
                              : pinnedSortDir === "desc"
                                ? "descending"
                                : pinnedSortDir === false
                                  ? "none"
                                  : undefined
                          return (
                            <TableHead
                              key={header.id}
                              aria-sort={pinnedAriaSort}
                              className="h-11 px-2 first:pl-4 last:pr-4 sticky top-0 z-20 bg-background align-middle text-left"
                              style={colWidthMeta.styleFor(header.column.id)}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                              {header.column.getCanResize() && (
                                <div
                                  onDoubleClick={() =>
                                    header.column.resetSize()
                                  }
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    header.getResizeHandler()(e);
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    header.getResizeHandler()(e);
                                  }}
                                  className="absolute right-0 top-0 h-full w-4 cursor-col-resize select-none touch-none flex items-center justify-center bg-transparent hover:bg-blue-500/10 z-10"
                                  style={{ userSelect: "none" }}
                                >
                                  <div
                                    className={`w-px h-[70%] border-r border-border hover:border-blue-500 ${
                                      header.column.getIsResizing()
                                        ? "border-blue-500 border-r-2"
                                        : ""
                                    }`}
                                  />
                                </div>
                              )}
                            </TableHead>
                          );
                        }

                        return (
                          <SortableTableHead
                            key={header.id}
                            headerId={header.column.id}
                            widthStyle={colWidthMeta.styleFor(header.column.id)}
                            width={header.getSize()}
                            minWidth={header.column.columnDef.minSize || 50}
                            maxWidth={header.column.columnDef.maxSize || 1000}
                            canResize={header.column.getCanResize()}
                            isResizing={header.column.getIsResizing()}
                            resizeHandler={header.getResizeHandler()}
                            resetSize={() => header.column.resetSize()}
                            ariaSortDir={header.column.getCanSort() ? header.column.getIsSorted() : undefined}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </SortableTableHead>
                        );
                      })}
                      {/* Trailing filler header cell — absorbs leftover width
                          when the columns are narrower than the container so the
                          real columns keep their exact widths (no shrink/snap
                          between populated and empty states). width:auto with
                          no min lets table-fixed give it all the slack. */}
                    </TableRow>
                  </SortableContext>
                );
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const rowClassName =
                    typeof rowBehavior?.className === "function"
                      ? rowBehavior.className(row.original)
                      : (rowBehavior?.className ?? "");

                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      // `group/row` lets cell renderers reveal action icons on
                      // row hover via `group-hover/row:opacity-100`. The
                      // densityClass above enforces row height so the row
                      // doesn't grow when content wraps.
                      className={cn(
                        "group/row",
                        rowBehavior?.onClick && "cursor-pointer",
                        rowClassName,
                      )}
                      onClick={
                        rowBehavior?.onClick
                          ? (e) => {
                              const target = e.target as HTMLElement;
                              const selector =
                                rowBehavior.interactiveSelector ??
                                DEFAULT_INTERACTIVE_SELECTOR;
                              const interactive = target.closest(selector);
                              if (interactive) return;
                              rowBehavior.onClick!(row.original);
                            }
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          // `align-middle` keeps the checkbox + status icons
                          // baseline-aligned with text in sibling cells, even
                          // when one cell renders a taller spinner glyph (B4).
                          // `overflow-hidden` clips wrapping content so row
                          // height stays uniform.
                          className="first:pl-4 last:pr-4 align-middle overflow-hidden"
                          style={colWidthMeta.styleFor(cell.column.id)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={columns.length}
                    className="h-px p-0 border-0"
                  >
                    {/* Spacer cell only — the actual empty/no-results content is
                        rendered as an absolute overlay over the scroll body
                        (below), so it always centers in the VISIBLE viewport
                        regardless of how wide the table-fixed columns make the
                        row. */}
                    &nbsp;
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>

          {/* Empty / no-results overlay — absolutely centered over the scroll
              body (which is `relative` + sized) so the content sits in the
              middle of the VISIBLE area below the frozen header, never pushed
              off-screen by a wide table-fixed row. Pinned under the sticky
              header via top-11 (header is h-11). */}
          {table.getRowModel().rows?.length === 0 && (
            <div className="pointer-events-none absolute inset-0 top-11 flex items-center justify-center px-4">
              <div className="pointer-events-auto flex flex-col items-center justify-center text-center">
                {/* Twin of the replace-mode guard above: only show the
                    first-time "create your first X" inline empty state when the
                    list is GENUINELY empty (no active search/filter). When a
                    search/filter emptied it, fall through to the
                    `hasActiveTableFilters` branch below so the user sees "No
                    results match your filters — Clear filters" with the search
                    box still visible, instead of a false "no data" claim. */}
                {emptyState?.mode === "inline" &&
                data.length === 0 &&
                !hasActiveTableFilters &&
                !isFetching ? (
                  <>
                    {emptyState.icon}
                    <p className="text-sm font-medium mt-2">{emptyState.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {emptyState.description}
                    </p>
                    {emptyState.actions && (
                      <div className="mt-4">{emptyState.actions}</div>
                    )}
                  </>
                ) : hasActiveTableFilters ? (
                  <>
                    <SearchX className="h-10 w-10 text-muted-foreground/60" />
                    <p className="text-sm font-medium mt-3">
                      No results match your filters
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try adjusting or clearing your search and filters.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={clearAllTableFilters}
                    >
                      <X className="h-3.5 w-3.5 mr-1.5" />
                      Clear filters
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{noResultsText}</p>
                )}
              </div>
            </div>
          )}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeHeaderId ? (
            <div className="bg-background border rounded-md shadow-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap opacity-90">
              {getDisplayName(activeHeaderId)}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Client-side pagination footer — driven by TanStack table state.
          Rendered when `clientPagination` is set AND server-side `pagination`
          is NOT (server wins). Reads filtered+sorted row count so the
          range matches what's actually displayed. */}
      {!pagination && clientPagination && (() => {
        const tablePageSize = table.getState().pagination.pageSize;
        const pageIndex = table.getState().pagination.pageIndex;
        const filteredCount = table.getFilteredRowModel().rows.length;
        const totalPages = Math.max(
          1,
          Math.ceil(filteredCount / Math.max(1, tablePageSize)),
        );
        const currentPage = pageIndex + 1;
        const start = filteredCount === 0 ? 0 : pageIndex * tablePageSize + 1;
        const end = Math.min((pageIndex + 1) * tablePageSize, filteredCount);
        const pageSizeOptions =
          clientPagination.pageSizeOptions ?? [10, 25, 50, 100];
        return (
          <div className="flex items-center justify-between p-4 border-t text-sm">
            <div className="text-muted-foreground text-xs">
              {totalPages <= 1 ? (
                <span>
                  {filteredCount.toLocaleString()} {entityNoun}
                </span>
              ) : (
                <span>
                  {start.toLocaleString()}-{end.toLocaleString()} of{" "}
                  {filteredCount.toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Rows</span>
                <Select
                  value={`${tablePageSize}`}
                  onValueChange={(value) => table.setPageSize(Number(value))}
                >
                  <SelectTrigger className="h-7 w-20 text-xs">
                    <SelectValue>{tablePageSize}</SelectValue>
                  </SelectTrigger>
                  <SelectContent side="top">
                    {pageSizeOptions.map((size) => (
                      <SelectItem key={size} value={`${size}`}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">
                {currentPage} / {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.setPageIndex(totalPages - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer — shown when there's page-based pagination OR a custom
          renderFooterStatus (cursor-based tables, e.g. the phishing Activity
          report, render their "Load more" here and pass no `pagination` prop;
          gating the whole footer on `pagination` silently dropped it). The
          page-nav controls below still require `pagination` and are guarded. */}
      {(pagination || renderFooterStatus) && (
        <div className="flex items-center justify-between p-4 border-t text-sm">
          <div className="text-muted-foreground text-xs">
            {(() => {
              // Bug #53 — when the "select all across pages" synthetic flag
              // is set, the rowSelection map only reflects the current page
              // (~25 rows), so using selectedRowCount would undercount in the
              // footer and contradict the banner (which correctly shows the
              // all-filtered count). Prefer filteredRowCount in that case.
              const effectiveSelectedCount = allFilteredRowsSelected
                ? filteredRowCount
                : selectedRowCount;
              if (renderFooterStatus) {
                return renderFooterStatus({
                  table,
                  selectedCount: effectiveSelectedCount,
                  filteredCount: pagination?.total ?? data.length,
                  displayedCount: data.length,
                  totalCount: pagination?.total ?? data.length,
                });
              }
              // No server pagination (footer still renders for custom filters /
              // row selection). There are no pages to range over, so show a
              // simple selected-or-total count and bail before the pagination.*
              // dereferences below — which would otherwise NPE at runtime and
              // fail tsc ('pagination' is possibly 'undefined').
              if (!pagination) {
                return effectiveSelectedCount > 0 ? (
                  <span>
                    {effectiveSelectedCount.toLocaleString()} selected
                  </span>
                ) : (
                  <span>
                    {data.length.toLocaleString()} {entityNoun}
                  </span>
                );
              }
              const start = (pagination.page - 1) * pagination.pageSize + 1;
              const end = Math.min(
                start + data.length - 1,
                pagination.total,
              );
              if (effectiveSelectedCount > 0) {
                return (
                  <span>
                    {effectiveSelectedCount.toLocaleString()} of{" "}
                    {pagination.total.toLocaleString()} selected
                  </span>
                );
              }
              // FIX R1-m5 — hide range+rows-per-page counter when single page
              if (pagination.totalPages <= 1) {
                return (
                  <span>{pagination.total.toLocaleString()} {entityNoun}</span>
                );
              }
              return (
                <span>
                  {start.toLocaleString()}-{end.toLocaleString()} of{" "}
                  {pagination.total.toLocaleString()}
                </span>
              );
            })()}
          </div>
          {pagination && (
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Rows</span>
              <Select
                value={`${pagination.pageSize}`}
                onValueChange={(value) =>
                  pagination.onPageSizeChange(Number(value))
                }
              >
                <SelectTrigger className="h-7 w-20 text-xs">
                  <SelectValue>{pagination.pageSize}</SelectValue>
                </SelectTrigger>
                <SelectContent side="top">
                  {(pagination.pageSizeOptions ?? [10, 25, 50, 100]).map(
                    (size) => (
                      <SelectItem key={size} value={`${size}`}>
                        {size}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground tabular-nums">
              {pagination.page} / {Math.max(pagination.totalPages, 1)}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(1)}
                disabled={pagination.page <= 1}
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Advanced Search Dialog */}
      {advancedSearch &&
        advancedSearchState &&
        advancedSearch.renderDialog({
          open: advancedSearchOpen,
          onOpenChange: setAdvancedSearchOpen,
          searchState: advancedSearchState,
          onApply: setAdvancedSearchState,
          onClear: () => setAdvancedSearchState(advancedSearch.emptyState),
        })}

      {/* Bulk Action Confirmation Dialog */}
      {confirmAction?.confirm && (
        <AlertDialog
          open={!!confirmAction}
          onOpenChange={(open) => !open && setConfirmAction(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmAction.confirm.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction.confirm.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={
                  confirmAction.confirm.destructive
                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    : ""
                }
                onClick={() => {
                  confirmAction.onExecute(selectedItems, clearSelection, {
                    allFilteredSelected: allFilteredRowsSelected,
                    filteredTotal: filteredRowCount,
                  });
                  setConfirmAction(null);
                }}
              >
                {confirmAction.confirm.confirmLabel || "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

